import type { TOrderStats } from '@/type';
import type {
  GetOrderByIdPayloadDto,
  GetOrderByIdResponseDto,
  GetOrdersStatsPayloadDto,
  GetOrdersStatsResponseDto,
  SearchOrderPayloadDto,
  SearchOrderResponseDto,
  UpdateOrderStatusPayloadDto,
  UpdateOrderStatusResponseDto,
} from '@/types/request.dto';
import {
  EPaymentMethod,
  EStoreThreadStatus,
  type StoreThread,
} from '@/types/store.entity';
import { uuidv4 } from '@/utils/uuid';
import { defineStore } from 'pinia';
import type { Socket } from 'socket.io-client';
import { computed, reactive, ref } from 'vue';
import { useAuthStore } from '../auth/auth.slice';
import { EStoreIdentifier } from '../store.constante';
import { orderService } from './orders.service';
import { useToast } from 'primevue';

export const useOrdersStore = defineStore(EStoreIdentifier.ORDER_STORE, () => {
  const authStore = useAuthStore();
  const allOrders = ref<Map<string, TOrderEntityResponse>>(new Map());
  const threads = ref<StoreThread<OrderStoreAction>[]>([]);
  const orderStats = ref<TOrderStats>();
  const toast = useToast();

  const dateSorter = (a: TOrderEntityResponse, b: TOrderEntityResponse) => {
    return new Date(a.updatedAt ?? a.createdAt).getTime() >
      new Date(b.updatedAt ?? b.createdAt).getTime()
      ? -1
      : 1;
  };

  const ordersInProgress = computed(() =>
    Array.from(allOrders.value.values())
      .filter((order) => ['in_progress'].includes(order?.status))
      .sort(dateSorter),
  );

  const ordersInPending = computed(() =>
    Array.from(allOrders.value.values())
      .filter((order) => ['pending'].includes(order?.status))
      .sort(dateSorter),
  );

  const ordersReady = computed(() =>
    Array.from(allOrders.value.values())
      .filter((order) => ['ready'].includes(order?.status))
      .sort(dateSorter),
  );

  const ordersDoneRejected = computed(() =>
    Array.from(allOrders.value.values())
      .filter((order) => order?.status === 'rejected')
      .sort(dateSorter),
  );

  const ordersDoneCompleted = computed(() =>
    Array.from(allOrders.value.values())
      .filter((order) =>
        ['completed', 'terminated', 'served'].includes(order?.status),
      )
      .sort(dateSorter),
  );

  const searchOrder = async (payload: SearchOrderPayloadDto) => {
    const requestId = uuidv4();
    const abortCrt = new AbortController();

    const thread = reactive<StoreThread<OrderStoreAction>>({
      id: requestId,
      status: EStoreThreadStatus.LOADING,
      type: OrderStoreAction.search,
    });
    threads.value.push(thread);

    const isError = computed(() => thread.status == EStoreThreadStatus.ERROR),
      isSuccess = computed(() => thread.status == EStoreThreadStatus.SUCCESS),
      isLoading = computed(() => thread.status == EStoreThreadStatus.LOADING);

    let result: SearchOrderResponseDto;

    try {
      result = await orderService.searchOrder(payload, {
        requestId,
        signal: abortCrt.signal,
        jwttoken: authStore?.tokens?.accessToken,
      });
      if (result?.data) {
        result.data?.forEach((order) => {
          allOrders.value.set(order.id, order);
        });
        thread.status = EStoreThreadStatus.SUCCESS;
      } else throw new Error();
    } catch (error) {
      thread.status = EStoreThreadStatus.ERROR;
    }

    return { isError, isSuccess, isLoading, result };
  };

  const getOrderById = async (payload: GetOrderByIdPayloadDto) => {
    const requestId = uuidv4();
    const abortCrt = new AbortController();

    const thread = reactive<StoreThread<OrderStoreAction>>({
      id: requestId,
      status: EStoreThreadStatus.LOADING,
      type: OrderStoreAction.getById,
    });
    threads.value.push(thread);

    const isError = computed(() => thread.status == EStoreThreadStatus.ERROR),
      isSuccess = computed(() => thread.status == EStoreThreadStatus.SUCCESS),
      isLoading = computed(() => thread.status == EStoreThreadStatus.LOADING);

    let result: GetOrderByIdResponseDto;

    try {
      result = await orderService.getOrderById(payload, {
        requestId,
        signal: abortCrt.signal,
        jwttoken: authStore?.tokens?.accessToken,
      });
      if (result?.data) {
        allOrders.value.set(result.data.id, result.data);
        thread.status = EStoreThreadStatus.SUCCESS;
      } else throw new Error();
    } catch (error) {
      thread.status = EStoreThreadStatus.ERROR;
    }

    return { isError, isSuccess, isLoading, result };
  };

  const updateOrderStatus = async (payload: UpdateOrderStatusPayloadDto) => {
    const requestId = uuidv4();
    const abortCrt = new AbortController();

    const thread = reactive<StoreThread<OrderStoreAction>>({
      id: requestId,
      status: EStoreThreadStatus.LOADING,
      type: OrderStoreAction.updateStatus,
    });
    threads.value.push(thread);

    const isError = computed(() => thread.status == EStoreThreadStatus.ERROR),
      isSuccess = computed(() => thread.status == EStoreThreadStatus.SUCCESS),
      isLoading = computed(() => thread.status == EStoreThreadStatus.LOADING);

    let result: UpdateOrderStatusResponseDto;

    try {
      result = await orderService.updateOrder(payload, {
        requestId,
        signal: abortCrt.signal,
        jwttoken: authStore?.tokens?.accessToken,
      });

      if (result?.data) {
        allOrders.value.set(result.data?.id, result.data);
        thread.status = EStoreThreadStatus.SUCCESS;
        toast.add({
          summary: 'Commande mis à jour',
          detail:
            result?.data?.status == 'in_progress'
              ? 'Commande en préparation'
              : '',
          severity: 'info',
          life: 2_000,
        });
      } else throw new Error();
    } catch (error) {
      thread.status = EStoreThreadStatus.ERROR;
    }

    return { result, isError, isSuccess, isLoading };
  };

  const getOrderStats = async (payload: GetOrdersStatsPayloadDto) => {
    const requestId = uuidv4();
    const abortCrt = new AbortController();

    const thread = reactive<StoreThread<OrderStoreAction>>({
      id: requestId,
      status: EStoreThreadStatus.LOADING,
      type: OrderStoreAction.getStats,
    });
    threads.value.push(thread);

    const isError = computed(() => thread.status == EStoreThreadStatus.ERROR),
      isSuccess = computed(() => thread.status == EStoreThreadStatus.SUCCESS),
      isLoading = computed(() => thread.status == EStoreThreadStatus.LOADING);

    let result: GetOrdersStatsResponseDto;

    try {
      result = await orderService.getOrderStats(
        { restauId: payload.restauId },
        {
          requestId,
          signal: abortCrt.signal,
          jwttoken: authStore?.tokens?.accessToken,
        },
      );

      if (result?.data) {
        orderStats.value = result.data;
        thread.status = EStoreThreadStatus.SUCCESS;
      } else throw new Error();
    } catch (error) {
      thread.status = EStoreThreadStatus.ERROR;
    }

    return { result, isError, isLoading, isSuccess };
  };

  const bindSocketEvents = (socketOrders: Socket) => {
    socketOrders.on(
      'order:new',
      (payload: { order: TOrderEntityResponse; stats: TOrderStats }) => {
        if (payload?.order?.paymentMethod == EPaymentMethod.CASH) {
          allOrders.value.set(payload.order?.id, payload.order);
          // Fetch full order to get product images (socket payload is partial)
          orderService
            .getOrderById(
              { orderId: payload.order.id },
              {
                requestId: uuidv4(),
                signal: new AbortController().signal,
                jwttoken: authStore?.tokens?.accessToken,
              },
            )
            .then((result) => {
              if (result?.data)
                allOrders.value.set(result.data.id, result.data);
            })
            .catch(() => {});
        }
        if (payload?.stats)
          getOrderStats({
            restauId: authStore?.decodedJwt?.accessToken?.restaurant_ref,
          });
      },
    );
    socketOrders.on(
      'order:updated',
      (payload: { order: TOrderEntityResponse; stats: TOrderStats }) => {
        if (allOrders.value.has(payload.order?.id)) {
          const existing = allOrders.value.get(payload.order?.id)!;
          allOrders.value.set(payload.order?.id, {
            ...payload.order,
            items: existing.items?.length
              ? existing.items
              : payload.order.items,
          });
          // Fetch full order to get product images (socket payload is partial)
          orderService
            .getOrderById(
              { orderId: payload.order.id },
              {
                requestId: uuidv4(),
                signal: new AbortController().signal,
                jwttoken: authStore?.tokens?.accessToken,
              },
            )
            .then((result) => {
              if (result?.data)
                allOrders.value.set(result.data.id, result.data);
            })
            .catch(() => {});
        }
        if (payload?.stats)
          getOrderStats({
            restauId: authStore?.decodedJwt?.accessToken?.restaurant_ref,
          });
      },
    );
    socketOrders.on(
      'order:paid',
      (payload: { order: TOrderEntityResponse; stats: TOrderStats }) => {
        allOrders.value.set(payload.order?.id, payload.order);
        orderService
          .getOrderById(
            { orderId: payload.order.id },
            {
              requestId: uuidv4(),
              signal: new AbortController().signal,
              jwttoken: authStore?.tokens?.accessToken,
            },
          )
          .then((result) => {
            if (result?.data) allOrders.value.set(result.data.id, result.data);
          })
          .catch(() => {});
        if (payload?.stats)
          getOrderStats({
            restauId: authStore?.decodedJwt?.accessToken?.restaurant_ref,
          });
      },
    );
  };

  return {
    allOrders,
    orderStats,
    ordersInProgress,
    ordersInPending,
    ordersReady,
    ordersDoneRejected,
    ordersDoneCompleted,
    searchOrder,
    getOrderById,
    updateOrderStatus,
    getOrderStats,
    bindSocketEvents,
  };
});

export type TOrderEntityResponse = SearchOrderResponseDto['data'][number];

export enum OrderStoreAction {
  submit = 'order/submitOrder',
  abord = 'order/abordOrder',
  updateStatus = 'order/updateStatus',
  getById = 'order/getOrderById',
  getStats = 'order/getOrderStats',
  search = 'order/searchOrders',
  addItemInCart = 'order/addItemInCart',
  updatecartItem = 'order/updatecartItem',
  getCartItem = 'order/getCartItem',
  searchCartItems = 'order/searchCartItems',
  removeCartItem = 'order/removeCartItem',
}
